"use client";

import { useMemo } from "react";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { Car, Building2, ShieldCheck, type LucideIcon } from "lucide-react";
import CatalogSearchBar from "@/components/layout/CatalogSearchBar";

export default function HeroSearch() {
  const t = useTranslations("Hero");
  type HeroQuickSlug = "quickAll" | "quickAutomatic" | "quickElectric" | "quickHybrid";
  const quickLinks = useMemo(
    (): { href: string; slug: HeroQuickSlug; icon?: LucideIcon }[] => [
      { href: "/cars", slug: "quickAll", icon: Car },
      { href: "/cars?transmission=automatic", slug: "quickAutomatic" },
      { href: "/cars?fuel_type=electric", slug: "quickElectric" },
      { href: "/cars?fuel_type=hybrid", slug: "quickHybrid" },
    ],
    []
  );

  return (
    <div className="border-b border-gray-100 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-950">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="-mx-4 flex items-center gap-1 overflow-x-auto border-b border-gray-50 py-2 scrollbar-none dark:border-gray-800/80 px-4 sm:mx-0 sm:justify-between sm:px-0">
          <div className="flex flex-shrink-0 items-center gap-1">
            {quickLinks.map(({ href, slug, icon: Icon }) => (
              <Link
                key={href}
                href={href}
                className="flex min-h-9 items-center gap-1.5 whitespace-nowrap rounded-lg px-2.5 py-2 text-xs text-gray-500 transition-colors hover:bg-red-50 hover:text-[#E63946] sm:px-3 sm:text-sm"
              >
                {Icon && <Icon size={12} className="flex-shrink-0" />}
                {t(slug)}
              </Link>
            ))}
          </div>
          <div className="ml-2 flex flex-shrink-0 items-center gap-2">
            <Link
              href="/cars?is_verified=true"
              className="flex min-h-9 items-center gap-1.5 whitespace-nowrap rounded-lg bg-emerald-50 px-2.5 py-2 text-xs font-medium text-emerald-600 transition-colors hover:bg-emerald-100 dark:bg-emerald-950/50 dark:text-emerald-400 dark:hover:bg-emerald-900/40"
            >
              <ShieldCheck size={11} />
              <span className="hidden sm:inline">{t("verified")}</span>
              <span className="sm:hidden">{t("verifiedShort")}</span>
            </Link>
            <Link
              href="/vendors"
              className="flex min-h-9 items-center gap-1.5 whitespace-nowrap rounded-lg px-2.5 py-2 text-xs text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200 sm:px-3"
            >
              <Building2 size={12} />
              {t("companies")}
            </Link>
          </div>
        </div>

        <div className="py-2.5">
          <CatalogSearchBar />
        </div>
      </div>
    </div>
  );
}
